import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import ExportToolbar from "@/components/ExportToolbar";
import DateRangePicker from "@/components/DateRangePicker";
import { useReportRange, formatRangeLabel, formatRangePretty, formatFYLabel } from "@/lib/useReportRange";
import { exportFinancialPDF, exportFinancialExcel } from "@/lib/exporters";
import YoYSnapshot from "@/components/YoYSnapshot";

// Stable references — defined at module scope so they don't trigger child re-renders.
const PL_YOY_METRICS = [
  { label: "Total Revenue", path: "revenue.total_revenue", varKey: "total_revenue" },
  { label: "Total Expenses", path: "expenses.total_expenses", varKey: "total_expenses" },
  { label: "Profit before Tax", path: "profit_before_tax", varKey: "profit_before_tax" },
  { label: "Profit for the Year", path: "profit_for_the_year", varKey: "profit_for_the_year" },
];
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { devError } from "@/lib/logger";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

/** Data-quality banner shown when the P&L detects party ledgers sitting in
 *  P&L groups. They are excluded from P&L math (so profit stays honest) but
 *  the user is prompted to permanently move them to Sundry Creditors /
 *  Debtors via ``POST /companies/{cid}/repair-ledger-groups``.
 *
 *  See services/inventory_valuation.py + server.py for the fix rationale
 *  (SIDDARTH TECH bug report, Feb 2026). */
function MisplacedPartyBanner({ companyId, parties, onFixed }) {
  const [busy, setBusy] = useState(false);
  const repair = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/repair-ledger-groups`);
      toast.success(
        `Moved ${data.count} ledger(s) to their correct group. Reports will now match.`,
        { duration: 6000 },
      );
      onFixed?.();
    } catch (e) {
      toast.error("Repair failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="border-2 border-amber-400 bg-amber-50 rounded p-3 flex items-start gap-3"
      data-testid="misplaced-party-banner"
    >
      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1 text-xs text-amber-900 leading-relaxed">
        <div className="font-semibold text-amber-900 uppercase tracking-wider mb-1">
          {parties.length} party ledger(s) grouped incorrectly
        </div>
        These ledgers are used as suppliers/customers in your vouchers but sit in a
        Profit &amp; Loss group. They&apos;ve been excluded from the P&amp;L above to keep
        profit honest, but this is a data-quality issue you should fix so reports
        stay consistent:
        <ul className="mt-1.5 list-disc pl-5 space-y-0.5">
          {parties.map((p) => (
            <li key={p.id} className="font-mono">
              <b>{p.name}</b> is in <i>{p.current_group}</i> — should be in{" "}
              <i>Sundry Creditors</i> / <i>Sundry Debtors</i>
            </li>
          ))}
        </ul>
      </div>
      <Button
        size="sm"
        onClick={repair}
        disabled={busy}
        className="bg-amber-600 hover:bg-amber-700 text-white flex-shrink-0"
        data-testid="btn-repair-party-groups"
      >
        {busy ? "Repairing…" : "Auto-repair now"}
      </Button>
    </div>
  );
}

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII", "XIII", "XIV", "XV", "XVI", "XVII", "XVIII", "XIX"];

function fmt(amount) {
  if (amount === 0 || amount == null) return "—";
  if (amount < 0) return `(${formatINR(Math.abs(amount))})`;
  return formatINR(amount);
}

function PLRow({ romanIdx, label, amount, bold, highlight, indent, testid }) {
  return (
    <tr className={highlight ? "bg-emerald-50 border-t-2 border-emerald-300" : ""}>
      <td className="font-mono text-xs text-gray-500 py-1.5 pl-3 align-top" style={{ width: 36 }}>{romanIdx != null ? ROMAN[romanIdx] : ""}</td>
      <td className={`py-1.5 ${bold ? "font-semibold text-gray-900" : "text-gray-700 text-xs"} ${indent ? "pl-8" : ""}`}>{label}</td>
      <td className="text-right text-[10px] text-gray-400 font-mono pr-3 align-top" style={{ width: 48 }}>—</td>
      <td className={`tx-num font-mono tabular-nums pr-3 align-top ${bold ? "font-semibold text-gray-900" : "text-gray-700"}`} data-testid={testid}>{fmt(amount)}</td>
    </tr>
  );
}

/**
 * Renders the sub-group total row, followed by each underlying ledger (name + amount).
 * Used to expand "Revenue from Operations", "Other Income", and each expense line into
 * its constituent ledger detail (mirrors TallyPrime's Schedule III drill-down view).
 */
function PLBucket({ romanIdx, label, bucket, bold, indent, testid, applied }) {
  const ledgers = bucket?.ledgers || [];
  return (
    <>
      <PLRow romanIdx={romanIdx} label={label} amount={bucket?.amount ?? 0} bold={bold} indent={indent} testid={testid} />
      {ledgers.map((l, i) => (
        <tr key={`${testid}-${i}`} className="text-[11px] text-gray-500" data-testid={`${testid}-ledger-${i}`}>
          <td></td>
          <td className={`${indent ? "pl-14" : "pl-10"} text-gray-500`}>
            {l.id ? (
              <Link
                to={`/reports/ledger/${l.id}?start=${applied?.start || ""}&end=${applied?.end || ""}`}
                className="text-blue-700 hover:underline"
                data-testid={`link-pl-ledger-${l.name}`}
                title="Open this ledger's statement — edits there will update this P&L."
              >
                {l.name}
              </Link>
            ) : l.name}
          </td>
          <td></td>
          <td className="text-right pr-3 font-mono tabular-nums">{fmt(l.amount)}</td>
        </tr>
      ))}
    </>
  );
}

function Inner() {
  const { activeId, active } = useCompany();
  const { range, setRange, applied, apply, params } = useReportRange();
  const dirty = range.start !== applied.start || range.end !== applied.end;
  const [data, setData] = useState(null);
  const [adj, setAdj] = useState({
    partners_remuneration: 0,
    current_tax: 0,
    earlier_year_tax: 0,
    deferred_tax: 0,
    exceptional_items: 0,
    extraordinary_items: 0,
    discontinuing_pretax: 0,
    discontinuing_tax: 0,
  });
  const [adjOpen, setAdjOpen] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  // Stable params for YoY snapshot so it doesn't refetch on every parent render.
  const yoyParams = useMemo(() => ({
    start: applied.start, end: applied.end,
    partners_remuneration: adj.partners_remuneration,
    current_tax: adj.current_tax,
    earlier_year_tax: adj.earlier_year_tax,
    deferred_tax: adj.deferred_tax,
  }), [applied.start, applied.end, adj.partners_remuneration, adj.current_tax, adj.earlier_year_tax, adj.deferred_tax]);

  useEffect(() => {
    if (!activeId) return;
    let mounted = true;
    api.get(`/companies/${activeId}/reports/profit-loss`, { params: { ...params, ...adj } })
      .then((r) => { if (mounted) setData(r.data); })
      .catch((err) => devError("P&L load failed", err));
    return () => { mounted = false; };
  }, [activeId, applied.start, applied.end, reloadTick, adj, params]);

  const setAdjField = (k, v) => setAdj((a) => ({ ...a, [k]: v === "" ? 0 : Number(v) }));
  const recompute = () => setReloadTick((t) => t + 1);

  const onExcel = () => {
    if (!data) return;
    const ledgerLines = (bucket) => (bucket?.ledgers || []).map((l) => ({ cells: ["", "    " + l.name, "", l.amount], type: "ledger" }));
    const rows = [
      { cells: ["I", "Revenue from Operations", "", data.revenue.revenue_from_operations.amount], type: "sub" },
      ...ledgerLines(data.revenue.revenue_from_operations),
      { cells: ["II", "Other Income", "", data.revenue.other_income.amount], type: "sub" },
      ...ledgerLines(data.revenue.other_income),
      { cells: ["III", "Total Revenue (I + II)", "", data.revenue.total_revenue], type: "total" },
      { cells: ["IV", "Expenses:", "", ""], type: "group" },
      { cells: ["", "Cost of Goods Sold", "", data.expenses.cost_of_goods_sold.amount], type: "sub" },
      ...ledgerLines(data.expenses.cost_of_goods_sold),
      { cells: ["", "Employee Benefits Expense", "", data.expenses.employee_benefits.amount], type: "sub" },
      ...ledgerLines(data.expenses.employee_benefits),
      { cells: ["", "Finance Costs", "", data.expenses.finance_costs.amount], type: "sub" },
      ...ledgerLines(data.expenses.finance_costs),
      { cells: ["", "Depreciation and Amortization Expense", "", data.expenses.depreciation_amortization.amount], type: "sub" },
      ...ledgerLines(data.expenses.depreciation_amortization),
      { cells: ["", "Other Expenses", "", data.expenses.other_expenses.amount], type: "sub" },
      ...ledgerLines(data.expenses.other_expenses),
      { cells: ["", "Total Expenses", "", data.expenses.total_expenses], type: "total" },
      { cells: ["V", "Profit before exceptional & extraordinary items, partners' remuneration and tax (III − IV)", "", data.profit_before_exceptional], type: "total" },
      { cells: ["VI", "Exceptional Items", "", data.exceptional_items], type: "sub" },
      { cells: ["VII", "Profit before extraordinary items (V − VI)", "", data.profit_before_extraordinary], type: "total" },
      { cells: ["VIII", "Extraordinary Items", "", data.extraordinary_items], type: "sub" },
      { cells: ["IX", "Profit before partners' remuneration and tax (VII − VIII)", "", data.profit_before_partners_remuneration_and_tax], type: "total" },
      { cells: ["X", "Partners' Remuneration", "", data.partners_remuneration], type: "sub" },
      { cells: ["XI", "Profit before tax (IX − X)", "", data.profit_before_tax], type: "total" },
      { cells: ["XII", "Tax Expense", "", data.tax_expense.total], type: "sub" },
      { cells: ["", "    Current Tax", "", data.tax_expense.current_tax], type: "ledger" },
      { cells: ["", "    Excess / Short Provision (Earlier Years)", "", data.tax_expense.earlier_year_adjustments], type: "ledger" },
      { cells: ["", "    Deferred Tax Charge / (Benefit)", "", data.tax_expense.deferred_tax_charge], type: "ledger" },
      { cells: ["XIII", "Profit from Continuing Operations (XI − XII)", "", data.profit_from_continuing_operations], type: "total" },
      { cells: ["XIV", "Profit / (Loss) from Discontinuing Operations (after tax)", "", data.profit_from_discontinuing_operations.after_tax], type: "sub" },
      { cells: ["XV", "PROFIT / (LOSS) FOR THE YEAR (XIII + XIV)", "", data.profit_for_the_year], type: "grand" },
    ];
    exportFinancialExcel(`profit-loss-${active?.name || "report"}`, [
      { name: "P&L", title: "Statement of Profit & Loss", headers: ["#", "Particulars", "Note", "Amount (Rs.)"], rows },
    ], { company: active?.name, subtitle: `${formatRangePretty(range)} — Schedule III (Non-Corporate)` });
  };

  const onPDF = () => {
    if (!data) return;
    const ledgerRows = (bucket) => (bucket?.ledgers || []).map((l) => ({ cells: ["", l.name, formatINR(l.amount)], type: "ledger" }));
    const rows = [
      { cells: ["I", "Revenue from Operations", formatINR(data.revenue.revenue_from_operations.amount)], type: "sub" },
      ...ledgerRows(data.revenue.revenue_from_operations),
      { cells: ["II", "Other Income", formatINR(data.revenue.other_income.amount)], type: "sub" },
      ...ledgerRows(data.revenue.other_income),
      { cells: ["III", "Total Revenue (I + II)", formatINR(data.revenue.total_revenue)], type: "total" },
      { cells: ["IV", "Expenses", ""], type: "group" },
      { cells: ["", "Cost of Goods Sold", formatINR(data.expenses.cost_of_goods_sold.amount)], type: "sub" },
      ...ledgerRows(data.expenses.cost_of_goods_sold),
      { cells: ["", "Employee Benefits Expense", formatINR(data.expenses.employee_benefits.amount)], type: "sub" },
      ...ledgerRows(data.expenses.employee_benefits),
      { cells: ["", "Finance Costs", formatINR(data.expenses.finance_costs.amount)], type: "sub" },
      ...ledgerRows(data.expenses.finance_costs),
      { cells: ["", "Depreciation and Amortization", formatINR(data.expenses.depreciation_amortization.amount)], type: "sub" },
      ...ledgerRows(data.expenses.depreciation_amortization),
      { cells: ["", "Other Expenses", formatINR(data.expenses.other_expenses.amount)], type: "sub" },
      ...ledgerRows(data.expenses.other_expenses),
      { cells: ["", "Total Expenses", formatINR(data.expenses.total_expenses)], type: "total" },
      { cells: ["V", "Profit before exceptional & extraordinary items, partners' remuneration and tax (III − IV)", formatINR(data.profit_before_exceptional)], type: "total" },
      { cells: ["VI", "Exceptional Items", formatINR(data.exceptional_items)], type: "sub" },
      { cells: ["VII", "Profit before extraordinary items (V − VI)", formatINR(data.profit_before_extraordinary)], type: "total" },
      { cells: ["VIII", "Extraordinary Items", formatINR(data.extraordinary_items)], type: "sub" },
      { cells: ["IX", "Profit before partners' remuneration and tax (VII − VIII)", formatINR(data.profit_before_partners_remuneration_and_tax)], type: "total" },
      { cells: ["X", "Partners' Remuneration", formatINR(data.partners_remuneration)], type: "sub" },
      { cells: ["XI", "Profit before tax (IX − X)", formatINR(data.profit_before_tax)], type: "total" },
      { cells: ["XII", "Tax Expense (total)", formatINR(data.tax_expense.total)], type: "sub" },
      { cells: ["", "Current Tax", formatINR(data.tax_expense.current_tax)], type: "ledger" },
      { cells: ["", "Excess / Short Provision (Earlier Years)", formatINR(data.tax_expense.earlier_year_adjustments)], type: "ledger" },
      { cells: ["", "Deferred Tax Charge / (Benefit)", formatINR(data.tax_expense.deferred_tax_charge)], type: "ledger" },
      { cells: ["XIII", "Profit from Continuing Operations (XI − XII)", formatINR(data.profit_from_continuing_operations)], type: "total" },
      { cells: ["XIV", "Profit / (Loss) from Discontinuing Operations (after tax)", formatINR(data.profit_from_discontinuing_operations.after_tax)], type: "sub" },
      { cells: ["XV", "PROFIT / (LOSS) FOR THE YEAR (XIII + XIV)", formatINR(data.profit_for_the_year)], type: "grand" },
    ];
    exportFinancialPDF(`profit-loss-${active?.name || "report"}`,
      "Statement of Profit and Loss",
      [{ title: "", headers: ["#", "Particulars", "Amount (Rs.)"], rows }],
      {
        company: active?.name,
        address: active?.address,
        phone: active?.phone,
        email: active?.email,
        gstin: active?.gstin,
        pan: active?.pan,
        fy: formatFYLabel(range),
        subtitle: `${formatRangePretty(range)}  -  Schedule III (Division I) Non-Corporate Entity`,
        date: applied.end,
        place: active?.state || "",
      });
  };

  return (
    <>
      <PageHeader
        title="Statement of Profit & Loss"
        subtitle={`${active?.name || ""}  •  ${formatRangePretty(applied)}  •  Schedule III (Non-Corporate)`}
        actions={<ExportToolbar onExcel={onExcel} onPDF={onPDF} onPrint={() => window.print()} />}
      />
      <div className="p-6 space-y-4" data-testid="profit-loss-page">
        <div className="flex flex-wrap items-end gap-3">
          <DateRangePicker start={range.start} end={range.end} onChange={setRange} onApply={apply} dirty={dirty} />
          <Button variant="outline" size="sm" onClick={() => setAdjOpen((o) => !o)} className="h-9" data-testid="btn-toggle-adjustments">
            {adjOpen ? "Hide" : "Show"} optional adjustments (Tax / Partners&apos; remuneration / Exceptional items)
          </Button>
        </div>

        {adjOpen && (
          <div className="border border-gray-200 rounded bg-white p-4" data-testid="pl-adjustments">
            <div className="text-xs font-semibold text-gray-700 mb-2">Manual Schedule III adjustments (figures the books don&apos;t track yet)</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                ["partners_remuneration", "Partners' Remuneration"],
                ["current_tax", "Current Tax"],
                ["earlier_year_tax", "Earlier-year Tax Adj."],
                ["deferred_tax", "Deferred Tax Charge"],
                ["exceptional_items", "Exceptional Items"],
                ["extraordinary_items", "Extraordinary Items"],
                ["discontinuing_pretax", "Discont. Ops (pre-tax)"],
                ["discontinuing_tax", "Discont. Ops Tax"],
              ].map(([k, label]) => (
                <div key={k}>
                  <Label className="text-[10px] uppercase tracking-wider text-gray-600">{label}</Label>
                  <Input type="number" min="0" value={adj[k]} onChange={(e) => setAdjField(k, e.target.value)} className="h-8 font-mono" data-testid={`adj-${k}`} />
                </div>
              ))}
            </div>
            <div className="mt-3 flex justify-end">
              <Button size="sm" onClick={recompute} className="h-8" data-testid="btn-apply-adjustments">Apply</Button>
            </div>
          </div>
        )}

        {!data && <div className="text-sm text-gray-500" data-testid="pl-loading">Loading…</div>}

        {data?.misplaced_parties?.length > 0 && (
          <MisplacedPartyBanner
            companyId={activeId}
            parties={data.misplaced_parties}
            onFixed={recompute}
          />
        )}

        {data && (
          <YoYSnapshot
            endpoint={`/companies/${activeId}/reports/profit-loss`}
            params={yoyParams}
            metrics={PL_YOY_METRICS}
            testid="pl-yoy"
          />
        )}

        {data && (
          <div className="border border-gray-200 rounded bg-white overflow-hidden">
            <div className="px-3 py-2 bg-gray-100 border-b border-gray-200 text-xs font-semibold text-gray-900 uppercase tracking-wider">
              Statement of Profit and Loss
            </div>
            <table className="w-full" data-testid="pl-table">
              <thead>
                <tr className="text-[10px] uppercase text-gray-500 border-b border-gray-200">
                  <th className="text-left pl-3 py-1.5 font-medium" style={{ width: 36 }}>#</th>
                  <th className="text-left py-1.5 font-medium">Particulars</th>
                  <th className="text-right py-1.5 font-medium pr-3" style={{ width: 48 }}>Note</th>
                  <th className="text-right py-1.5 font-medium pr-3 w-44">Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                <PLBucket romanIdx={0} label="Revenue from Operations" bucket={data.revenue.revenue_from_operations} testid="pl-rev-ops" applied={applied} />
                <PLBucket romanIdx={1} label="Other Income" bucket={data.revenue.other_income} testid="pl-other-income" applied={applied} />
                <PLRow romanIdx={2} label="Total Revenue (I + II)" amount={data.revenue.total_revenue} bold testid="pl-total-revenue" />

                <tr className="border-t border-gray-200">
                  <td className="font-mono text-xs text-gray-500 py-1.5 pl-3">{ROMAN[3]}</td>
                  <td className="font-semibold text-gray-900 py-1.5">Expenses:</td>
                  <td colSpan={2}></td>
                </tr>
                <PLBucket label="Cost of Goods Sold" bucket={data.expenses.cost_of_goods_sold} indent testid="pl-cogs" applied={applied} />
                <PLBucket label="Employee Benefits Expense" bucket={data.expenses.employee_benefits} indent testid="pl-emp-benefits" applied={applied} />
                <PLBucket label="Finance Costs" bucket={data.expenses.finance_costs} indent testid="pl-finance" applied={applied} />
                <PLBucket label="Depreciation and Amortization Expense" bucket={data.expenses.depreciation_amortization} indent testid="pl-depreciation" applied={applied} />
                <PLBucket label="Other Expenses" bucket={data.expenses.other_expenses} indent testid="pl-other-expenses" applied={applied} />
                <PLRow label="Total Expenses" amount={data.expenses.total_expenses} indent bold testid="pl-total-expenses" />

                <PLRow romanIdx={4} label="Profit before exceptional and extraordinary items, partners' remuneration and tax (III - IV)" amount={data.profit_before_exceptional} bold testid="pl-pbe-exceptional" />
                <PLRow romanIdx={5} label="Exceptional Items" amount={data.exceptional_items} testid="pl-exceptional" />
                <PLRow romanIdx={6} label="Profit before extraordinary items, partners' remuneration and tax (V - VI)" amount={data.profit_before_extraordinary} bold testid="pl-pbe-extraordinary" />
                <PLRow romanIdx={7} label="Extraordinary Items" amount={data.extraordinary_items} testid="pl-extraordinary" />
                <PLRow romanIdx={8} label="Profit before partners' remuneration and tax (VII - VIII)" amount={data.profit_before_partners_remuneration_and_tax} bold testid="pl-pbt-rem" />
                <PLRow romanIdx={9} label="Partners' Remuneration" amount={data.partners_remuneration} testid="pl-partners-rem" />
                <PLRow romanIdx={10} label="Profit before tax (IX - X)" amount={data.profit_before_tax} bold testid="pl-pbt" />

                <tr className="border-t border-gray-200">
                  <td className="font-mono text-xs text-gray-500 py-1.5 pl-3">{ROMAN[11]}</td>
                  <td className="font-semibold text-gray-900 py-1.5">Tax Expense:</td>
                  <td colSpan={2}></td>
                </tr>
                <PLRow label="Current Tax" amount={data.tax_expense.current_tax} indent testid="pl-tax-current" />
                <PLRow label="Excess / Short Provision (Earlier Years)" amount={data.tax_expense.earlier_year_adjustments} indent testid="pl-tax-earlier" />
                <PLRow label="Deferred Tax Charge / (Benefit)" amount={data.tax_expense.deferred_tax_charge} indent testid="pl-tax-deferred" />
                <PLRow label="Total Tax Expense" amount={data.tax_expense.total} indent bold testid="pl-tax-total" />

                <PLRow romanIdx={12} label="Profit for the period from Continuing Operations (XI - XII)" amount={data.profit_from_continuing_operations} bold testid="pl-cont-ops" />
                <PLRow romanIdx={13} label="Profit / (Loss) from Discontinuing Operations (after tax)" amount={data.profit_from_discontinuing_operations.after_tax} testid="pl-discont-ops" />
                <PLRow romanIdx={14} label="PROFIT / (LOSS) FOR THE YEAR (XIII + XIV)" amount={data.profit_for_the_year} bold highlight testid="pl-final" />
              </tbody>
            </table>
          </div>
        )}

        <div className="text-[10px] text-gray-400 italic">
          The accompanying notes are an integral part of the financial statements. Schedule III (Division I) format for Non-Corporate Entity.
        </div>
      </div>
    </>
  );
}

export default function ProfitLoss() {
  return (
    <RequireCompany>
      <Inner />
    </RequireCompany>
  );
}
